import React, { useEffect, useRef } from 'react';
import ApexCharts, { ApexOptions } from 'apexcharts';
import { getCSSVariableValue } from '../../../../_mexablock/assets/ts/_utils';
import { formatWithCommas } from '../../../../_mexablock/helpers';

type Props = {
  height: number;
  series: any[];
  colors: any[];
  show: boolean;
  categories: string[];
  total: number;
  vigencia: string;
};

const GraficaTransaccionesData: React.FC<Props> = ({
  height,
  series,
  colors,
  categories,
  total,
  vigencia,
  show,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<ApexCharts | null>(null);

  useEffect(() => {
    if (!chartRef.current || !show) return;

    const options = getChartOptions(height, series, colors, categories);

    if (!chartInstance.current) {
      chartInstance.current = new ApexCharts(chartRef.current, options);
      chartInstance.current.render();
    } else {
      chartInstance.current.updateOptions(options, true, true);
    }

    return () => {
      chartInstance.current?.destroy();
      chartInstance.current = null;
    };
  }, [height, series, colors, categories, show]);

  return (
    <div className="card">
      <div className="card-header border-0 pt-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label text-muted fw-bold fs-5 mb-1">
            Datos Adquiridos
          </span>
          <span className=" fw-bolder fs-3">{formatWithCommas(total)} MB</span>
        </h3>
        <div className="text-primary fs-8 mt-2">Vigencia: {vigencia}</div>
      </div>
      <div className="card-body">
        <div ref={chartRef} id="kt_charts_widget_3_chart"></div>
      </div>
    </div>
  );
};

export { GraficaTransaccionesData };

function getChartOptions(
  height: number,
  _series: any | null,
  _colors: any,
  categories: string[] = []
): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500');
  const borderColor = getCSSVariableValue('--bs-gray-200');
  const baseColor = getCSSVariableValue('--bs-primary');

  return {
    series: _series,
    chart: {
      fontFamily: 'inherit',
      type: 'area',
      height: height,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {},
    legend: {
      show: true,
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'solid',
      opacity: 0,
    },
    stroke: {
      curve: 'straight',
      show: true,
      width: 2,
      colors: _colors,
    },
    xaxis: {
      categories: categories,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
      crosshairs: {
        position: 'front',
        stroke: {
          color: baseColor,
          width: 1,
          dashArray: 3,
        },
      },
      tooltip: {
        enabled: true,
        formatter: undefined,
        offsetY: 0,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
        formatter: function (val) {
          return `${formatWithCommas(val)} MB`;
        },
      },
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: function (val) {
          return `${formatWithCommas(val)} MB`;
        },
      },
    },
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    markers: {
      strokeWidth: 3,
    },
  };
}
